import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { Send, ArrowLeft, Search, User, Check, CheckCheck } from "lucide-react";

const MessagesPage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchMembers();
  }, []);

  useEffect(() => {
    if (userId) {
      loadConversation(userId);
    }
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API}/messages/inbox`),
        axios.get(`${API}/messages/sent`)
      ]);

      // Combine and dedupe by user
      const allMessages = [...inboxRes.data, ...sentRes.data];
      const userMap = new Map();
      
      allMessages.forEach(msg => {
        const otherId = msg.sender_id === user?.user_id ? msg.recipient_id : msg.sender_id;
        const otherName = msg.sender_id === user?.user_id ? "You" : msg.sender_name;
        
        if (!userMap.has(otherId) || new Date(msg.created_at) > new Date(userMap.get(otherId).created_at)) {
          userMap.set(otherId, {
            user_id: otherId,
            name: msg.sender_id === user?.user_id ? "Recipient" : msg.sender_name,
            lastMessage: msg.content,
            created_at: msg.created_at,
            unread: msg.sender_id !== user?.user_id && !msg.read
          });
        }
      });

      setConversations(Array.from(userMap.values()).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ));
    } catch (error) {
      console.error("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/users/members`);
      setMembers(response.data.filter(m => m.user_id !== user?.user_id));
    } catch (error) {
      console.error("Failed to fetch members");
    }
  };

  const loadConversation = async (otherId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${otherId}`);
      setMessages(response.data);
      
      // Find user info
      const member = members.find(m => m.user_id === otherId);
      if (member) {
        setSelectedUser(member);
      } else {
        // Try to get from conversation
        const conv = conversations.find(c => c.user_id === otherId);
        if (conv) {
          setSelectedUser({ user_id: otherId, name: conv.name });
        }
      }
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    navigate(`/messages/${u.user_id}`);
    loadConversation(u.user_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        recipient_id: selectedUser.user_id,
        content: newMessage
      });
      setMessages([...messages, response.data]);
      setNewMessage("");
      fetchConversations();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString();
  };

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] lg:h-screen flex" data-testid="messages-page">
        {/* Sidebar - Conversations */}
        <div className={`
          ${selectedUser ? 'hidden md:flex' : 'flex'}
          w-full md:w-80 lg:w-96 flex-col border-r border-[#E5DFEC] bg-white
        `}>
          <div className="p-4 border-b border-[#E5DFEC]">
            <EditableText
              blockKey="messages.title"
              fallback="Messages"
              as="h1"
              className="text-xl font-medium text-[#6B4E8A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members..."
                className="pl-10 bg-[#FAF7F2] border-[#E5DFEC]"
                data-testid="search-members-input"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {searchQuery ? (
              // Search results
              <div className="p-2">
                <p className="text-xs text-[#64748B] px-2 py-1">Members</p>
                {filteredMembers.map((m) => (
                  <button
                    key={m.user_id}
                    onClick={() => handleSelectUser(m)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F0ECF7] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium">
                      {m.name?.[0] || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-[#6B4E8A]">{m.name}</p>
                      <p className="text-xs text-[#64748B]">{m.province}</p>
                    </div>
                  </button>
                ))}
                {filteredMembers.length === 0 && (
                  <p className="text-center text-[#64748B] py-4">No members found</p>
                )}
              </div>
            ) : (
              // Conversations
              <div className="p-2">
                {conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <button
                      key={conv.user_id}
                      onClick={() => handleSelectUser(conv)}
                      className={`
                        w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left
                        ${selectedUser?.user_id === conv.user_id ? 'bg-[#F0ECF7]' : 'hover:bg-[#FAF7F2]'}
                      `}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#6B4E8A] text-white flex items-center justify-center font-medium">
                        {conv.name?.[0] || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-[#6B4E8A]">{conv.name}</p>
                          <span className="text-xs text-[#64748B]">{formatTime(conv.created_at)}</span>
                        </div>
                        <p className="text-sm text-[#64748B] truncate">{conv.lastMessage}</p>
                      </div>
                      {conv.unread && (
                        <div className="w-2 h-2 bg-[#8DB5A0] rounded-full"></div>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Search for members to start messaging</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`
          ${selectedUser ? 'flex' : 'hidden md:flex'}
          flex-1 flex-col bg-[#FAF7F2]
        `}>
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 p-4 bg-white border-b border-[#E5DFEC]">
                <button 
                  onClick={() => {
                    setSelectedUser(null);
                    navigate("/messages");
                  }}
                  className="md:hidden p-2 hover:bg-[#F0ECF7] rounded-lg"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium">
                  {selectedUser.name?.[0] || 'U'}
                </div>
                <div>
                  <p className="font-medium text-[#6B4E8A]">{selectedUser.name}</p>
                  {selectedUser.verification_status === "verified" && (
                    <span className="text-xs text-[#10B981]">Verified</span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, idx) => {
                  const isOwn = msg.sender_id === user?.user_id;
                  return (
                    <motion.div
                      key={msg.message_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`
                        max-w-[70%] p-4 rounded-2xl
                        ${isOwn 
                          ? 'bg-[#6B4E8A] text-white rounded-br-sm' 
                          : 'bg-white text-[#6B4E8A] border border-[#E5DFEC] rounded-bl-sm'}
                      `}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${isOwn ? 'text-white/70' : 'text-[#64748B]'}`}>
                          <span>{formatTime(msg.created_at)}</span>
                          {isOwn && (
                            msg.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-[#E5DFEC]">
                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="message-input"
                  />
                  <Button
                    type="submit"
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-6"
                    disabled={sending || !newMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <div className="w-16 h-16 rounded-full bg-[#F0ECF7] flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-[#64748B]" />
                </div>
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Select a conversation
                </h3>
                <p className="text-[#64748B] text-sm">
                  Choose from your existing conversations or search for a member to start a new one.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MessagesPage;
